import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot, Cpu, RefreshCw, CheckCircle, XCircle, Play, AlertCircle,
  BarChart3, Clock, Zap, HardDrive
} from 'lucide-react';
import { aiAgent } from '../../../services/api/client';
import { toast } from '../../../utils/toastUtils';

const AIAgentModels = () => {
  const [modelsStatus, setModelsStatus] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusData, statsData] = await Promise.all([
        aiAgent.getModels(),
        aiAgent.getModelsStats()
      ]);
      setModelsStatus(statusData);
      setUsageStats(statsData);
    } catch (err) {
      console.error('Failed to fetch models data:', err);
      toast.error('Erreur lors du chargement des modèles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTestModel = async (modelName) => {
    setTesting(modelName);
    try {
      const result = await aiAgent.testModel(modelName);
      if (result.success) {
        toast.success(`SYLink AI opérationnel (${result.response_time_ms}ms)`);
      } else {
        toast.error(`Erreur: ${result.error}`);
      }
    } catch (err) {
      toast.error('Erreur lors du test');
    } finally {
      setTesting(null);
    }
  };

  // Ollama status badge
  const getOllamaStatusBadge = (status) => {
    const config = {
      running: { color: 'bg-green-500/20 text-green-400 border-green-500/30', text: 'En ligne' },
      stopped: { color: 'bg-red-500/20 text-red-400 border-red-500/30', text: 'Arrete' },
      error: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', text: 'Erreur' },
    };
    const cfg = config[status] || config.error;
    return (
      <span className={`px-3 py-1 rounded-full border text-sm font-medium ${cfg.color}`}>
        {cfg.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-400" />
            SYLink AI
          </h3>
          <p className="text-gray-400 text-sm mt-1">Status des modèles SYLink AI utilisés par l'agent</p>
        </div>
        <div className="flex items-center gap-3">
          {modelsStatus && getOllamaStatusBadge(modelsStatus.ollama_status)}
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 bg-brand-black-lighter rounded-lg text-gray-400 hover:text-brand-green"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* VRAM Info */}
      {modelsStatus && (modelsStatus.total_vram_mb || modelsStatus.available_vram_mb) && (
        <div className="bg-brand-black rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Cpu className="w-6 h-6 text-purple-400" />
              <div>
                <p className="text-white font-medium">GPU VRAM</p>
                <p className="text-gray-500 text-sm">Mémoire disponible pour les modèles</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-purple-400">
                {((modelsStatus.available_vram_mb || 0) / 1024).toFixed(1)} GB
              </p>
              <p className="text-gray-500 text-sm">
                / {((modelsStatus.total_vram_mb || 0) / 1024).toFixed(1)} GB
              </p>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full h-2 bg-gray-700 rounded">
              <div
                className="h-full rounded bg-purple-500"
                style={{
                  width: `${modelsStatus.total_vram_mb
                    ? ((modelsStatus.total_vram_mb - modelsStatus.available_vram_mb) / modelsStatus.total_vram_mb) * 100
                    : 0}%`
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Default Model */}
      {modelsStatus?.default_model && (
        <div className="bg-brand-black rounded-xl p-4 border border-brand-green/30">
          <div className="flex items-center gap-2 text-brand-green mb-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Modèle par défaut</span>
          </div>
          <p className="text-white text-lg font-bold">SYLink AI</p>
        </div>
      )}

      {/* Models List */}
      <div className="space-y-4">
        <h4 className="text-gray-400 text-sm font-medium">Modèles Disponibles</h4>
        {modelsStatus?.models && modelsStatus.models.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {modelsStatus.models.map((model) => (
              <div
                key={model.name}
                className={`bg-brand-black rounded-xl p-4 border ${
                  model.is_loaded ? 'border-green-500/30' : 'border-gray-800'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Bot className={`w-5 h-5 ${model.is_loaded ? 'text-green-400' : 'text-gray-500'}`} />
                    <span className="text-white font-medium">SYLink AI</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {model.is_loaded ? (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                        Charge
                      </span>
                    ) : model.is_available ? (
                      <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded">
                        Disponible
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                        Non disponible
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  {model.size_gb && (
                    <div className="flex items-center gap-1 text-gray-400">
                      <HardDrive className="w-3 h-3" />
                      <span>{model.size_gb.toFixed(1)} GB</span>
                    </div>
                  )}
                  {model.vram_required_mb && (
                    <div className="flex items-center gap-1 text-gray-400">
                      <Cpu className="w-3 h-3" />
                      <span>{(model.vram_required_mb / 1024).toFixed(1)} GB VRAM</span>
                    </div>
                  )}
                </div>

                {model.task_types && model.task_types.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {model.task_types.map((task) => (
                      <span
                        key={task}
                        className="px-2 py-0.5 bg-brand-black-lighter text-gray-400 text-xs rounded"
                      >
                        {task}
                      </span>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => handleTestModel(model.name)}
                  disabled={testing === model.name || !model.is_available}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 disabled:opacity-50"
                >
                  {testing === model.name ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Tester
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun modele detecte</p>
          </div>
        )}
      </div>

      {/* Usage Stats */}
      {usageStats && (
        <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-brand-green" />
            Statistiques d'Utilisation ({usageStats.period})
          </h4>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-brand-black-lighter rounded-lg">
              <p className="text-gray-500 text-xs">Total Requetes</p>
              <p className="text-xl font-bold text-white">{usageStats.total_requests || 0}</p>
            </div>
            <div className="text-center p-3 bg-brand-black-lighter rounded-lg">
              <p className="text-gray-500 text-xs">Tokens Utilises</p>
              <p className="text-xl font-bold text-purple-400">{usageStats.total_tokens_used || 0}</p>
            </div>
          </div>

          {/* Requests by Model */}
          {usageStats.requests_by_model && Object.keys(usageStats.requests_by_model).length > 0 && (
            <div className="mb-4">
              <p className="text-gray-500 text-sm mb-2">Requetes par modele</p>
              <div className="space-y-2">
                {Object.entries(usageStats.requests_by_model).map(([model, count]) => (
                  <div key={model} className="flex items-center justify-between bg-brand-black-lighter p-2 rounded-lg">
                    <span className="text-gray-400 text-sm">SYLink AI</span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Latency by Model */}
          {usageStats.avg_latency_by_model && Object.keys(usageStats.avg_latency_by_model).length > 0 && (
            <div className="mb-4">
              <p className="text-gray-500 text-sm mb-2">Latence moyenne (ms)</p>
              <div className="space-y-2">
                {Object.entries(usageStats.avg_latency_by_model).map(([model, latency]) => (
                  <div key={model} className="flex items-center justify-between bg-brand-black-lighter p-2 rounded-lg">
                    <span className="text-gray-400 text-sm">SYLink AI</span>
                    <span className="flex items-center gap-1 text-white font-medium">
                      <Clock className="w-3 h-3 text-gray-500" />
                      {latency.toFixed(0)}ms
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success Rate by Model */}
          {usageStats.success_rate_by_model && Object.keys(usageStats.success_rate_by_model).length > 0 && (
            <div>
              <p className="text-gray-500 text-sm mb-2">Taux de succes</p>
              <div className="space-y-2">
                {Object.entries(usageStats.success_rate_by_model).map(([model, rate]) => (
                  <div key={model} className="flex items-center justify-between bg-brand-black-lighter p-2 rounded-lg">
                    <span className="text-gray-400 text-sm">SYLink AI</span>
                    <span className={`font-medium ${rate > 90 ? 'text-green-400' : rate > 70 ? 'text-orange-400' : 'text-red-400'}`}>
                      {rate.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAgentModels;
